// Image caching utility to preload and cache team logos
class ImageCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
    this.preloadQueue = [];
    this.isPreloading = false;
    this.serviceWorkerManager = null;
  }

  // Initialize service worker manager
  async initializeServiceWorker() {
    if (!this.serviceWorkerManager) {
      const { serviceWorkerManager } = await import('./serviceWorker');
      this.serviceWorkerManager = serviceWorkerManager;
      await this.serviceWorkerManager.register();
    }
    return this.serviceWorkerManager;
  }

  // Preload an image and cache it
  async preloadImage(url) {
    if (this.cache.has(url)) {
      return this.cache.get(url);
    }

    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }

    const loadPromise = new Promise((resolve, reject) => {
      // Get the JWT token from localStorage
      const token = localStorage.getItem("token");

      if (!token) {
        reject(new Error("Authentication required"));
        return;
      }

      // Fetch the image with authentication headers
      fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          return response.blob();
        })
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);

          // Store the object URL in cache
          this.cache.set(url, objectUrl);
          this.loadingPromises.delete(url);
          resolve(objectUrl);
        })
        .catch((error) => {
          this.loadingPromises.delete(url);
          reject(error);
        });
    });

    this.loadingPromises.set(url, loadPromise);
    return loadPromise;
  }

  // Preload multiple images with concurrency control
  async preloadImages(urls, concurrency = 6) {
    const results = [];
    
    for (let i = 0; i < urls.length; i += concurrency) {
      const batch = urls.slice(i, i + concurrency);
      const batchPromises = batch.map((url) =>
        this.preloadImage(url).catch((error) => {
          console.warn(`Failed to preload image: ${url}`, error);
          return null;
        })
      );
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      
      // Small delay between batches to prevent overwhelming the server
      if (i + concurrency < urls.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    
    return results;
  }

  // Get cached image URL or return null
  getCachedImage(url) {
    return this.cache.get(url) || null;
  }

  // Check if image is cached
  isCached(url) {
    return this.cache.has(url);
  }

  // Clear cache
  clear() {
    // Revoke all object URLs before clearing
    for (const objectUrl of this.cache.values()) {
      URL.revokeObjectURL(objectUrl);
    }
    this.cache.clear();
    this.loadingPromises.clear();
  }

  // Clear cache and force reload
  clearAndReload() {
    // Revoke all object URLs before clearing
    for (const objectUrl of this.cache.values()) {
      URL.revokeObjectURL(objectUrl);
    }
    this.cache.clear();
    this.loadingPromises.clear();
  }

  // Get cache size
  size() {
    return this.cache.size;
  }
}

// Create a singleton instance
export const imageCache = new ImageCache();

// React hook for using cached images
import { useState, useEffect } from "react";

export const useCachedImage = (url, fallbackUrl) => {
  const [imageUrl, setImageUrl] = useState(fallbackUrl);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) {
      setImageUrl(fallbackUrl);
      setIsLoading(false);
      return;
    }

    // Check if already cached
    if (imageCache.isCached(url)) {
      const cachedUrl = imageCache.getCachedImage(url);
      setImageUrl(cachedUrl);
      setIsLoading(false);
      return;
    }

    // Start loading
    setIsLoading(true);
    setError(null);

    imageCache
      .preloadImage(url)
      .then((objectUrl) => {
        setImageUrl(objectUrl);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err);
        setImageUrl(fallbackUrl);
        setIsLoading(false);
      });
  }, [url, fallbackUrl]);

  return { imageUrl, isLoading, error };
};

import { getTeamHelmetUrl } from "./helmetGenerator";

// Utility to preload all team logos when app starts
export const preloadTeamLogos = async (teams) => {
  if (!teams || teams.length === 0) {
    console.log('No teams provided for preloading');
    return;
  }

  console.log('Starting logo preloading for', teams.length, 'teams');

  // Initialize service worker
  await imageCache.initializeServiceWorker();

  // Try to load actual logos if available
  const logoUrls = teams
    .map((team) => team.logoUrl)
    .filter((url) => url && url !== "");

  console.log('Found', logoUrls.length, 'logo URLs to preload');

  // Also preload helmet fallbacks for all teams
  const helmetUrls = teams.map((team) => getTeamHelmetUrl(team));

  try {
    // Try bulk preloading first for better performance
    if (logoUrls.length > 0) {
      try {
        const teamAbbreviations = teams.map(team => team.abbreviation).join(',');
        const baseUrl = logoUrls[0].split('/logos/')[0];
        const bulkUrl = `${baseUrl}/logos/bulk/${teamAbbreviations}`;
        
        const token = localStorage.getItem("token");
        const response = await fetch(bulkUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Cache the bulk-loaded logos in memory
          data.logos.forEach(logoData => {
            const objectUrl = `data:${logoData.contentType};base64,${logoData.data}`;
            const logoUrl = `${baseUrl}/logos/${logoData.team}`;
            imageCache.cache.set(logoUrl, objectUrl);
          });
          
          // Also cache in service worker for persistence
          try {
            await imageCache.serviceWorkerManager.cacheLogos(logoUrls);
          } catch (swError) {
            console.warn('Service Worker caching failed:', swError);
          }
          
          console.log(`Successfully preloaded ${data.logos.length} team logos via bulk endpoint`);
        } else {
          throw new Error('Bulk preload failed');
        }
      } catch (bulkError) {
        console.warn('Bulk preload failed, falling back to individual requests:', bulkError);
        // Fallback to individual requests
        await imageCache.preloadImages(logoUrls, 4);
        console.log(`Successfully preloaded ${logoUrls.length} team logos via individual requests`);
      }
    }

    // Then preload helmet fallbacks
    if (helmetUrls.length > 0) {
      await imageCache.preloadImages(helmetUrls, 8);
      console.log(`Successfully preloaded ${helmetUrls.length} helmet fallbacks`);
    }

  } catch (error) {
    console.warn('Some images failed to preload:', error);
  }
};
